
import { isEmbedMode } from "./embedMode";

const PREFIX = "__zv_embed_ls:";

function installEmbedStorageIsolation(): void {
  if (typeof window === "undefined" || !isEmbedMode()) return;

  let backing: Storage;
  try {
    backing = window.sessionStorage;
    backing.setItem(`${PREFIX}__probe`, "1");
    backing.removeItem(`${PREFIX}__probe`);
  } catch {
    return;
  }

  const ownKeys = (): string[] => {
    const keys: string[] = [];
    for (let i = 0; i < backing.length; i++) {
      const key = backing.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key.slice(PREFIX.length));
    }
    return keys;
  };

  const api = {
    getItem: (key: string): string | null => backing.getItem(PREFIX + String(key)),
    setItem: (key: string, value: string): void => backing.setItem(PREFIX + String(key), String(value)),
    removeItem: (key: string): void => backing.removeItem(PREFIX + String(key)),
    clear: (): void => ownKeys().forEach((key) => backing.removeItem(PREFIX + key)),
    key: (index: number): string | null => ownKeys()[index] ?? null,
    get length(): number {
      return ownKeys().length;
    },
  };

  const isApiKey = (prop: PropertyKey) => typeof prop !== "string" || prop in api;
  const isolated = new Proxy(api, {
    get: (target, prop, receiver) =>
      isApiKey(prop) ? Reflect.get(target, prop, receiver) : backing.getItem(PREFIX + String(prop)) ?? undefined,
    set: (target, prop, value, receiver) => {
      if (isApiKey(prop)) return Reflect.set(target, prop, value, receiver);
      backing.setItem(PREFIX + String(prop), String(value));
      return true;
    },
    has: (target, prop) => isApiKey(prop) || backing.getItem(PREFIX + String(prop)) !== null,
    deleteProperty: (target, prop) => {
      if (!isApiKey(prop)) backing.removeItem(PREFIX + String(prop));
      return true;
    },
    ownKeys: () => ownKeys(),
    getOwnPropertyDescriptor: (target, prop) => {
      if (isApiKey(prop)) return undefined;
      const value = backing.getItem(PREFIX + String(prop));
      return value === null ? undefined : { value, writable: true, enumerable: true, configurable: true };
    },
  }) as unknown as Storage;

  try {
    Object.defineProperty(window, "localStorage", { configurable: true, get: () => isolated });
  } catch {
  }
}

installEmbedStorageIsolation();
